import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Heart, ChevronDown, ChevronUp, Clock, Users, Flame,
  UtensilsCrossed, ExternalLink, ShoppingCart, ChefHat,
  Sparkles, ArrowLeft, Trash2, Loader2, Star
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useListContext } from "../contexts/ListContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FavoriteRecipeCard = ({ fav, onUnfavorite, lists, currentListId, setLists }) => {
  const [expanded, setExpanded] = useState(false);
  const [addListId, setAddListId] = useState(currentListId);
  const recipe = fav.recipe;
  if (!recipe) return null;

  const hasIngredients = recipe.ingredients && recipe.ingredients.length > 0;
  const hasInstructions = recipe.instructions && recipe.instructions.length > 0;
  const hasMissing = hasIngredients && recipe.ingredients.some(ing => !ing.have);

  const titleCase = (str) => str.replace(/\b\w/g, c => c.toUpperCase());

  const addMissingToList = () => {
    const listId = addListId || currentListId;
    const targetList = lists.find(l => l.id === listId);
    if (!targetList) return;
    const existing = new Set((targetList.items || []).map(i => i.name.toLowerCase()));
    const missing = recipe.ingredients.filter(ing => !ing.have);
    const newItems = missing
      .filter(ing => !existing.has(ing.name.toLowerCase()))
      .map((ing, idx) => ({
        id: Date.now() + Math.random() + idx,
        name: titleCase(ing.name),
        quantity: 1,
        completed: false,
        category: "Other",
        estimated_price: 2.99,
        source: "recipe-suggestion"
      }));
    if (newItems.length > 0) {
      setLists(prev => prev.map(list =>
        list.id === listId ? { ...list, items: [...newItems, ...list.items] } : list
      ));
    }
  };

  const getDifficultyColor = (diff) => {
    if (diff === "Easy") return "bg-green-100 text-green-700";
    if (diff === "Medium") return "bg-amber-100 text-amber-700";
    return "bg-red-100 text-red-700";
  };

  return (
    <Card className="bg-white hover:shadow-md transition-shadow overflow-hidden" data-testid={`fav-card-${fav.fav_id}`}>
      <CardContent className="p-0">
        <div className="p-4 cursor-pointer" onClick={() => setExpanded(!expanded)}>
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                {fav.source === "spoonacular" && recipe.image && (
                  <img src={recipe.image} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                )}
                <div>
                  <h3 className="font-bold text-gray-900 leading-tight">{recipe.name}</h3>
                  {recipe.description && (
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{recipe.description}</p>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={(e) => { e.stopPropagation(); onUnfavorite(fav.fav_id); }}
                className="p-1.5 rounded-full text-red-500 bg-red-50 hover:bg-red-100 transition-colors"
                data-testid={`unfav-btn-${fav.fav_id}`}
              >
                <Heart className="h-4 w-4 fill-current" />
              </button>
              {expanded ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {recipe.prep_time && (
              <span className="flex items-center gap-1 text-xs text-gray-600">
                <Clock className="h-3 w-3" />{recipe.prep_time}
              </span>
            )}
            {recipe.cook_time && recipe.cook_time !== "? min" && (
              <span className="flex items-center gap-1 text-xs text-gray-600">
                <Flame className="h-3 w-3" />{recipe.cook_time}
              </span>
            )}
            {recipe.servings > 0 && (
              <span className="flex items-center gap-1 text-xs text-gray-600">
                <Users className="h-3 w-3" />{recipe.servings}
              </span>
            )}
            {recipe.difficulty && (
              <Badge className={`text-xs ${getDifficultyColor(recipe.difficulty)}`}>{recipe.difficulty}</Badge>
            )}
            {recipe.category && <Badge variant="outline" className="text-xs capitalize">{recipe.category}</Badge>}
            <Badge className={`text-xs ${fav.source === "ai" ? "bg-purple-100 text-purple-700" : fav.source === "meal-plan" ? "bg-blue-100 text-blue-700" : "bg-orange-100 text-orange-700"}`}>
              {fav.source === "ai" ? "AI Generated" : fav.source === "meal-plan" ? "Meal Plan" : "Tasty Picks"}
            </Badge>
          </div>

          {hasIngredients && (
            <div className="mt-3 flex flex-wrap gap-1">
              {recipe.ingredients.slice(0, 5).map((ing, i) => (
                <span key={i} className={`text-xs px-2 py-0.5 rounded-full ${
                  ing.have ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                }`}>
                  {ing.name}
                </span>
              ))}
              {recipe.ingredients.length > 5 && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                  +{recipe.ingredients.length - 5} more
                </span>
              )}
            </div>
          )}
        </div>

        {expanded && (
          <div className="border-t px-4 pb-4 pt-3 bg-gray-50/50 space-y-4">
            {hasIngredients && (
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-2">
                  <UtensilsCrossed className="h-3.5 w-3.5 inline mr-1" />Ingredients
                </h4>
                <div className="space-y-1">
                  {recipe.ingredients.map((ing, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${ing.have ? "bg-green-500" : "bg-gray-300"}`} />
                      <span className="text-gray-500 min-w-[100px] text-xs">{ing.amount}</span>
                      <span className={ing.have ? "text-gray-900" : "text-gray-500"}>{ing.name}</span>
                      {!ing.have && <Badge variant="outline" className="text-xs ml-auto text-gray-400">need</Badge>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {hasInstructions && (
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-2">Instructions</h4>
                <ol className="space-y-2">
                  {recipe.instructions.map((step, i) => (
                    <li key={i} className="flex gap-2 text-sm">
                      <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0 text-xs font-bold">
                        {i + 1}
                      </span>
                      <span className="text-gray-700">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {recipe.tips && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-800"><span className="font-semibold">Chef's Tip:</span> {recipe.tips}</p>
              </div>
            )}

            {recipe.source_url && (
              <a href={recipe.source_url} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
                <ExternalLink className="h-3 w-3" /> View original recipe
              </a>
            )}

            {hasMissing && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <select
                    value={addListId || ''}
                    onChange={(e) => setAddListId(Number(e.target.value))}
                    className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-xs bg-white"
                    data-testid={`fav-list-select-${fav.fav_id}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {lists.map((list) => (
                      <option key={list.id} value={list.id}>{list.name}</option>
                    ))}
                  </select>
                  <Button
                    variant="outline" size="sm"
                    className="border-amber-300 text-amber-700 hover:bg-amber-50 text-xs whitespace-nowrap"
                    onClick={(e) => { e.stopPropagation(); addMissingToList(); }}
                    data-testid={`fav-add-missing-${fav.fav_id}`}
                  >
                    <ShoppingCart className="h-3.5 w-3.5 mr-1" />Add Missing
                  </Button>
                </div>
              </div>
            )}

            <Button
              variant="ghost" size="sm"
              className="text-red-500 hover:text-red-600 hover:bg-red-50 text-xs w-full"
              onClick={(e) => { e.stopPropagation(); onUnfavorite(fav.fav_id); }}
              data-testid={`unfav-full-btn-${fav.fav_id}`}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1" />Remove from Favorites
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Favorites = () => {
  const navigate = useNavigate();
  const { lists, currentListId, setLists } = useListContext();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/recipes/favorites`);
        if (res.ok) {
          const data = await res.json();
          setFavorites(data.favorites || []);
        }
      } catch (e) {
        console.error("Failed to fetch favorites:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchFavorites();
  }, []);

  const handleUnfavorite = async (favId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/recipes/favorites/${favId}`, { method: "DELETE" });
      if (res.ok) {
        setFavorites(prev => prev.filter(f => f.fav_id !== favId));
      }
    } catch (e) {
      console.error("Failed to unfavorite:", e);
    }
  };

  const aiRecipes = favorites.filter(f => f.source === "ai");
  const spoonRecipes = favorites.filter(f => f.source === "spoonacular");
  const mealPlanFavs = favorites.filter(f => f.source === "meal-plan");

  const filteredFavorites = activeTab === "all"
    ? favorites
    : activeTab === "ai" ? aiRecipes 
    : activeTab === "meal-plan" ? mealPlanFavs
    : spoonRecipes;

  const tabs = [
    { key: "all", label: "All", count: favorites.length },
    { key: "meal-plan", label: "Meal Plans", count: mealPlanFavs.length },
    { key: "ai", label: "AI Generated", count: aiRecipes.length },
    { key: "spoonacular", label: "Tasty Picks", count: spoonRecipes.length },
  ];

  return (
    <div className="p-4 space-y-5" data-testid="favorites-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost" size="sm"
          onClick={() => navigate("/recipes")}
          className="text-gray-600 hover:text-gray-900 -ml-2 p-2"
          data-testid="back-to-recipes-btn"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-gray-900">Saved Recipes</h2>
          <p className="text-gray-500 text-sm">{favorites.length} recipe{favorites.length !== 1 ? "s" : ""} saved</p>
        </div>
        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
          <Heart className="h-5 w-5 text-red-500 fill-current" />
        </div>
      </div>

      {/* Tabs */}
      {favorites.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {tabs.map(tab => (
            <Button
              key={tab.key}
              variant={activeTab === tab.key ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveTab(tab.key)}
              className={`whitespace-nowrap text-xs ${
                activeTab === tab.key
                  ? "bg-red-500 hover:bg-red-600 text-white"
                  : "border-gray-300 text-gray-600"
              }`}
              data-testid={`tab-${tab.key}`}
            >
              {tab.key === "ai" && <Sparkles className="h-3 w-3 mr-1" />}
              {tab.key === "meal-plan" && <Star className="h-3 w-3 mr-1" />}
              {tab.key === "spoonacular" && <ChefHat className="h-3 w-3 mr-1" />}
              {tab.label} ({tab.count})
            </Button>
          ))}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex flex-col items-center py-16 gap-3">
          <Loader2 className="h-8 w-8 text-red-400 animate-spin" />
          <p className="text-gray-500 text-sm">Loading saved recipes...</p>
        </div>
      )}

      {/* Recipe Cards */}
      {!loading && filteredFavorites.length > 0 && (
        <div className="space-y-3">
          {filteredFavorites.map(fav => (
            <FavoriteRecipeCard
              key={fav.fav_id}
              fav={fav}
              onUnfavorite={handleUnfavorite}
              lists={lists}
              currentListId={currentListId}
              setLists={setLists}
            />
          ))}
        </div>
      )}

      {/* Empty States */}
      {!loading && favorites.length === 0 && (
        <Card className="border-dashed border-2 border-gray-200">
          <CardContent className="p-10 text-center">
            <Heart className="h-14 w-14 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 font-semibold text-lg">No saved recipes yet</p>
            <p className="text-sm text-gray-400 mt-2 max-w-xs mx-auto">
              Tap the heart icon on any recipe to save it here for quick access later.
            </p>
            <Button
              className="mt-6 bg-amber-500 hover:bg-amber-600 text-white"
              onClick={() => navigate("/recipes")}
              data-testid="go-to-recipes-btn"
            >
              <ChefHat className="h-4 w-4 mr-2" />Discover Recipes
            </Button>
          </CardContent>
        </Card>
      )}

      {!loading && favorites.length > 0 && filteredFavorites.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <ChefHat className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No {activeTab === "ai" ? "AI generated" : activeTab === "meal-plan" ? "meal plan" : "Tasty Picks"} recipes saved</p>
            <p className="text-sm text-gray-400 mt-1">Try a different filter tab</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Favorites;
